import { apiFetch } from './client'

/**
 * Tela-piloto de login (S25-10) - chamadas de autenticacao por senha.
 * Contrato: `apps/api/routes/auth.py` (`POST /api/auth/login` e `/login/mfa`),
 * DTOs em `core_platform/identity_access/application/dtos.py`.
 *
 * DECISAO S22-5 (declarada em `apps/api/session_cookie.py`): o corpo do
 * `/login/mfa` AINDA traz `access_token` e `refresh_token`; a sessao do navegador
 * vive no cookie `HttpOnly`. O criterio "token nunca acessivel por JavaScript"
 * passa a depender de a SPA NAO guardar o corpo. Por isso `loginSecondFactor`
 * devolve SO o usuario, e os tokens nem sao tipados aqui: nao existe caminho de
 * codigo que os leia. A trava e o teste `auth.test.ts` (dimensao D9).
 */

export type NextStep = 'mfa_email' | 'mfa_totp' | 'password_change' | 'mfa_enroll' | 'mfa_email_verify'

export interface LoginTicket {
  ticket: string
  nextStep: NextStep | string
  expiresIn: number
}

export interface SessionUser {
  id: string
  tenantId: string
  username: string
  email: string
}

interface LoginTicketResponse {
  ticket: string
  ticket_type: string
  expires_in: number
  next_step: string
}

interface UserResponse {
  id: string
  tenant_id: string
  username: string
  email: string
}

/** Tokens deliberadamente AUSENTES do tipo (S22-5). */
interface SessionResponse {
  user: UserResponse
}

/**
 * S31-8 (2026-09-25): a EMPRESA e descoberta pelo servidor a partir do dominio
 * do e-mail digitado. Por padrao a SPA NAO envia `tenant_id`. `VITE_TENANT_ID`
 * continua existindo so como FORCAMENTO de desenvolvimento (o tenant de dev
 * `tenant-test-001` pode nao ter dominio cadastrado); vazio = o servidor decide.
 */
export function resolveTenantId(): string {
  return import.meta.env.VITE_TENANT_ID ?? ''
}

export async function loginPassword(input: {
  tenantId: string
  username: string
  password: string
}): Promise<LoginTicket> {
  const body: Record<string, string> = { username: input.username, password: input.password }
  if (input.tenantId) body.tenant_id = input.tenantId
  const r = await apiFetch<LoginTicketResponse>('/api/auth/login', { method: 'POST', body })
  return { ticket: r.ticket, nextStep: r.next_step, expiresIn: r.expires_in }
}

export async function loginSecondFactor(input: {
  ticket: string
  code: string
}): Promise<SessionUser> {
  const r = await apiFetch<SessionResponse>('/api/auth/login/mfa', {
    method: 'POST',
    body: { ticket: input.ticket, code: input.code },
  })
  const u = r.user
  return { id: u.id, tenantId: u.tenant_id, username: u.username, email: u.email }
}

/** Codigo digitado: remove espacos (o usuario copia do e-mail com espaco). */
export function normalizeCode(raw: string): string {
  return raw.replace(/\s+/g, '')
}

/** B1: encerra a sessao no servidor (invalida a sessao e expira o cookie, ADR-0024 D5.4). */
export async function logout(): Promise<void> {
  await apiFetch<unknown>('/api/auth/logout', { method: 'POST' })
}
